import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import DramaCard from '@/components/base/DramaCard';
import Gallery from '@/pages/detail/components/Gallery';
import { dramas } from '@/mocks/dramas';
import { actors } from '@/mocks/actors';
import { useFavorites } from '@/hooks/useFavorites';

const sampleReviews = [
  { name: '影迷·阿哲', rating: 9, date: '2024-11-25', text: '节奏克制但情绪很足，看完之后久久缓不过来。演员的微表情处理得特别好。' },
  { name: '深夜观察者', rating: 8, date: '2024-11-20', text: '画面和配乐都很讲究，短剧能拍出这种质感真的不容易，推荐给喜欢慢节奏的朋友。' },
  { name: '小满', rating: 9, date: '2024-11-15', text: '成年人的故事就是这样，没有大起大落，但每一帧都戳心。已经二刷了。' },
];

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  const drama = useMemo(() => dramas.find((d) => d.id === id), [id]);

  const related = useMemo(() => {
    if (!drama) return [];
    const sameGenre = dramas.filter(
      (d) => d.id !== drama.id && d.genres.some((g) => drama.genres.includes(g)),
    );
    const rest = dramas.filter((d) => d.id !== drama.id && !sameGenre.includes(d));
    return [...sameGenre, ...rest].slice(0, 6);
  }, [drama]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(''), 2200);
  };

  if (!drama) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-600" />
          <h1 className="mt-4 text-xl font-semibold text-foreground-100">该短剧信息暂时不可用</h1>
          <p className="mt-2 text-sm text-foreground-400">可能内容已下架或链接有误</p>
          <Link to="/category" className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            返回分类浏览
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const favorited = isFavorite(drama.id);
  const castList = drama.cast
    .map((c) => ({ ...c, actor: actors.find((a) => a.id === c.actorId) }))
    .filter((c) => c.actor);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/category" className="hover:text-primary-400 transition-colors cursor-pointer">短剧分类</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300">{drama.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Main content */}
          <div className="lg:col-span-2 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1 rounded-md bg-primary-100/15 px-2.5 py-1 text-xs font-medium text-primary-300">
                <i className="ri-star-fill text-[10px]" /> {drama.rating.toFixed(1)} 分
              </span>
              {drama.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category?genre=${encodeURIComponent(g)}`}
                  className="rounded-md bg-secondary-900/60 px-2.5 py-1 text-xs text-secondary-200 hover:bg-secondary-800 transition-colors cursor-pointer"
                >
                  {g}
                </Link>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading leading-tight">{drama.title}</h1>
            <p className="mt-3 text-sm text-foreground-400">
              {drama.year} · {drama.region} · {drama.episodes} · {drama.duration}
            </p>

            <div className="mt-6">
              <Gallery images={drama.gallery} title={drama.title} />
            </div>

            {/* Synopsis */}
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-50 mb-4">
                <span className="block w-1 h-5 rounded-full bg-primary-500" />
                剧情简介
              </h2>
              <p className="text-sm md:text-base leading-loose text-foreground-300">{drama.synopsis}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {drama.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-accent-900/50 px-3 py-1 text-xs text-accent-200">
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            {/* Cast */}
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-50 mb-4">
                <span className="block w-1 h-5 rounded-full bg-primary-500" />
                主要演员
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {castList.map((item) => (
                  <Link
                    key={item.actorId}
                    to={`/actor/${item.actorId}`}
                    className="group flex items-center gap-3 rounded-lg border border-background-800 bg-background-900/60 p-3 hover:border-primary-700/70 transition-colors cursor-pointer"
                  >
                    <div className="h-14 w-12 shrink-0 overflow-hidden rounded-md bg-background-800">
                      <img src={item.actor!.avatar} alt={`${item.actor!.name} 演员照`} title={`${item.actor!.name} - 成人短剧演员资料`} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-50 truncate group-hover:text-primary-300 transition-colors">
                        {item.actor!.name}
                      </h3>
                      <p className="mt-0.5 text-xs text-foreground-500 truncate">饰 {item.role}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* User reviews */}
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-50 mb-4">
                <span className="block w-1 h-5 rounded-full bg-primary-500" />
                用户评价
                <span className="text-xs font-normal text-foreground-500">（示例数据）</span>
              </h2>
              <div className="flex flex-col gap-3">
                {sampleReviews.map((review) => (
                  <div key={review.name} className="rounded-lg border border-background-800 bg-background-900/50 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-800 text-sm font-medium text-secondary-100">
                          {review.name.slice(-2, -1)}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground-100">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.date}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-accent-300">{review.rating}.0</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-300">{review.text}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 flex flex-col gap-5">
              <div className="overflow-hidden rounded-lg border border-background-800 bg-background-900/60">
                <div className="relative w-full aspect-[2/3] bg-background-800">
                  <img src={drama.poster} alt={`${drama.title} 海报`} title={`${drama.title} - 成人短剧详情`} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5">
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={() => toggleFavorite(drama.id)}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        favorited
                          ? 'bg-primary-600 text-background-50'
                          : 'border border-background-600 bg-background-900 text-foreground-100 hover:border-primary-500 hover:text-primary-300'
                      }`}
                    >
                      <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} />
                      {favorited ? '已收藏' : '收藏'}
                    </button>
                    <button
                      type="button"
                      onClick={() => showToast('分享链接已复制（演示）')}
                      className="flex items-center justify-center gap-2 rounded-md border border-background-600 bg-background-900 px-4 py-2.5 text-sm font-medium text-foreground-100 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-share-forward-line" />
                      分享
                    </button>
                  </div>

                  <dl className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">导演</dt>
                      <dd className="text-foreground-100 text-right">{drama.director}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">类型</dt>
                      <dd className="text-foreground-100 text-right">{drama.genres.join(' / ')}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">制片地区</dt>
                      <dd className="text-foreground-100 text-right">{drama.region}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">集数时长</dt>
                      <dd className="text-foreground-100 text-right">{drama.episodes}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">发行日期</dt>
                      <dd className="text-foreground-100 text-right">{drama.airDate}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">热度</dt>
                      <dd className="text-foreground-100 text-right">{(drama.views / 10000).toFixed(1)}万 播放</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Related */}
        <section className="mt-14">
          <h2 className="flex items-center gap-2 text-lg md:text-xl font-bold text-foreground-50 mb-5">
            <span className="block w-1 h-5 rounded-full bg-accent-500" />
            相关推荐
          </h2>
          <div className="flex gap-3 md:gap-4 overflow-x-auto no-scrollbar pb-2">
            {related.map((d) => (
              <DramaCard key={d.id} drama={d} className="w-[150px] md:w-[180px] shrink-0" />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-md bg-foreground-50 px-4 py-2.5 text-sm font-medium text-background-950 animate-fade-up">
          <i className="ri-check-line mr-1.5" />
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}